import React, { useState, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import * as ReactDOM from 'react-dom';
import { photoStore } from './photoStore';
import { Button, FormControl, FormLabel } from 'react-bootstrap';
import Camera, { FACING_MODES } from 'react-html5-camera-photo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toastr } from 'react-redux-toastr';
import { useDispatch, useSelector } from 'react-redux';
import { TFunction } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Iphoto } from '../../models';
import { addNewPhoto, deletePhoto, updatePhoto } from '../../actions/photosActions';
import { initialPhoto } from '../../reducers/initialState';
import { IinitialState } from '../../reducers';
import { selectPhotosByJobComment, selectPhotosByMeasurementPoint } from '../../reducers/photosReducer';
import { cameraManager } from './CameraSingleton';
import 'react-html5-camera-photo/build/css/index.css';

const iconCamera = require('../../images/icons/icon-camera.svg').default;
const iconPhoto = require('../../images/icons/icon-photo.svg').default;
const maxPhotoSize = 31457280;
const defaultMaxWidth = 1200;

export interface InlinePhotoControlHandle {
    commitChanges: () => Promise<void>;
    resetChanges: () => void;
    clearPhotos: () => void;
}

interface InlinePhotoControlProps {
    jobID: string;
    installBaseID: string;
    jobCommentID?: string;
    measurementPointID?: string;
    /*
     * View only: the photos of a visit recorded on a closed job can be looked at, but not added to,
     * deleted, or moved in or out of the report (MMG-4345).
     */
    readOnly?: boolean;
    t: TFunction;
}

interface PendingAdd {
    id: string;
    previewUrl: string;
    isInReport: boolean;
}

const resizeImage = async (file: Blob, maxWidth = defaultMaxWidth): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            const scaleFactor = maxWidth / img.width;
            const targetWidth = img.width * scaleFactor;
            const targetHeight = img.height * scaleFactor;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            ctx?.drawImage(img, 0, 0, targetWidth, targetHeight);
            canvas.toBlob(blob => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Error creating blob from canvas'));
                }
            }, file.type);
        };
        img.onerror = () => {
            reject(new Error('Error loading image'));
        };
    });
};

const InlinePhotoControl = forwardRef<InlinePhotoControlHandle, InlinePhotoControlProps>(
    ({ jobID, installBaseID, jobCommentID, measurementPointID, readOnly = false, t }, ref) => {
        const dispatch = useDispatch();
        const [showCamera, setShowCamera] = useState(false);
        const fileInputRef = useRef<HTMLInputElement>(null);

        // Multi-photo pending state
        const [pendingAdds, setPendingAdds] = useState<PendingAdd[]>([]);
        const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
        const [pendingReportChanges, setPendingReportChanges] = useState<Map<string, boolean>>(new Map());

        const existingPhotos = useSelector((state: IinitialState) => {
            if (jobCommentID) {
                return selectPhotosByJobComment(state, { jobCommentID });
            }
            if (measurementPointID) {
                return selectPhotosByMeasurementPoint(state, { measurementPointID, installBaseID });
            }
            return [];
        });

        const stagePhoto = useCallback((blob: Blob, id: string) => {
            setPendingAdds(prev => [...prev, {
                id,
                previewUrl: window.URL.createObjectURL(blob),
                isInReport: false
            }]);
        }, []);

        const handleTakePhoto = (dataUri: string) => {
            const base64Length = dataUri.length - (dataUri.indexOf(',') + 1);
            const padding =
                dataUri.charAt(dataUri.length - 2) === '=' ? 2 : dataUri.charAt(dataUri.length - 1) === '=' ? 1 : 0;
            const fileSize = base64Length * 0.75 - padding;

            if (fileSize > maxPhotoSize) {
                toastr.error(t('toastMessage:invalidFormSubmission'), '', { timeOut: 3000 });
                return;
            }

            const id = uuidv4();
            fetch(dataUri)
                .then(result => result.blob())
                .then(data => resizeImage(data, defaultMaxWidth))
                .then(resizedBlob => {
                    const resizedFile = new File([resizedBlob], id, { type: resizedBlob.type });
                    return photoStore.setItem(id, resizedFile).then(() => resizedBlob);
                })
                .then(resizedBlob => {
                    cameraManager.hide();
                    setShowCamera(false);
                    stagePhoto(resizedBlob, id);
                })
                .catch(err => {
                    console.error('[InlinePhotoControl handleTakePhoto]:', err);
                    toastr.error(t('toastMessage:photoStorageError'), '', { timeOut: 3000 });
                });
        };

        const handleCameraError = (error: any) => {
            cameraManager.hide();
            setShowCamera(false);
            toastr.confirm(error.message, { okText: t('common:ok'), disableCancel: true });
        };

        const handleFileChange = async (fileList: FileList) => {
            const files = Array.from(fileList);
            for (let i = 0; i < files.length; i++) {
                const originalFile = files[i];
                if (!originalFile) continue;

                if (originalFile.size > maxPhotoSize) {
                    toastr.error(t('toastMessage:invalidFormSubmission'), '', { timeOut: 3000 });
                    continue;
                }

                const id = uuidv4();
                try {
                    const resizedBlob = await resizeImage(originalFile, defaultMaxWidth);
                    const resizedFile = new File([resizedBlob], originalFile.name, { type: resizedBlob.type });
                    await photoStore.setItem(id, resizedFile);
                    stagePhoto(resizedBlob, id);
                } catch (error) {
                    console.error('[InlinePhotoControl handleFileChange]:', error);
                    toastr.error(t('toastMessage:photoStorageError'), '', { timeOut: 3000 });
                }
            }
        };

        const confirmDelete = (onOk: () => void) => {
            toastr.confirm(t('common:deleteConfirm'), {
                onOk,
                onCancel: () => { /* no-op */ },
                okText: t('common:ok'),
                cancelText: t('common:cancel')
            });
        };

        const handleDeleteExisting = (photoId: string) => {
            confirmDelete(() => setPendingDeleteIds(prev => new Set(prev).add(photoId)));
        };

        const handleDeletePending = (photoId: string) => {
            confirmDelete(() => {
                photoStore.removeItem(photoId);
                setPendingAdds(prev => prev.filter(p => p.id !== photoId));
            });
        };

        const toggleExistingReport = (photoId: string, currentValue: boolean) => {
            setPendingReportChanges(prev => {
                const next = new Map(prev);
                // Find the original value from the existing photo
                const photo = existingPhotos.find(p => p.id === photoId);
                const originalValue = photo?.isInReport ?? false;
                const newValue = !currentValue;
                // If toggling back to original, remove the pending change
                if (newValue === originalValue) {
                    next.delete(photoId);
                } else {
                    next.set(photoId, newValue);
                }
                return next;
            });
        };

        const togglePendingReport = (photoId: string) => {
            setPendingAdds(prev => prev.map(p =>
                p.id === photoId ? { ...p, isInReport: !p.isInReport } : p
            ));
        };

        // Exposed to parent via ref
        useImperativeHandle(ref, () => ({
            commitChanges: async () => {
                // Delete photos marked for deletion
                pendingDeleteIds.forEach(id => {
                    dispatch(deletePhoto(id, installBaseID) as any);
                });
                // Update isInReport changes on existing photos
                pendingReportChanges.forEach((isInReport, id) => {
                    const photo = existingPhotos.find(p => p.id === id);
                    if (photo) {
                        dispatch(updatePhoto({ ...photo, isInReport }) as any);
                    }
                });
                // Add new photos
                pendingAdds.forEach(pending => {
                    const photo: Iphoto = {
                        ...initialPhoto,
                        id: pending.id,
                        jobID,
                        installBaseID,
                        isInReport: pending.isInReport,
                        comments: '',
                        jobCommentID: jobCommentID || '',
                        measurementPointID: measurementPointID || ''
                    };
                    dispatch(addNewPhoto(photo) as any);
                });
                // Clear pending state
                setPendingAdds([]);
                setPendingDeleteIds(new Set());
                setPendingReportChanges(new Map());
            },
            resetChanges: () => {
                // Clean up staged images from localForage
                pendingAdds.forEach(p => photoStore.removeItem(p.id));
                setPendingAdds([]);
                setPendingDeleteIds(new Set());
                setPendingReportChanges(new Map());
            },
            clearPhotos: () => {
                // Mark all existing photos for deletion
                existingPhotos.forEach(p => {
                    setPendingDeleteIds(prev => new Set(prev).add(p.id));
                });
                // Remove all pending adds
                pendingAdds.forEach(p => photoStore.removeItem(p.id));
                setPendingAdds([]);
                setPendingReportChanges(new Map());
            }
        }), [pendingAdds, pendingDeleteIds, pendingReportChanges, existingPhotos, dispatch, installBaseID, jobID, jobCommentID, measurementPointID]);

        const handleCameraOpen = () => {
            cameraManager.show();
            setShowCamera(true);
        };

        const handleCameraClose = () => {
            cameraManager.hide();
            setShowCamera(false);
        };

        const cameraComponent = showCamera
            ? ReactDOM.createPortal(
                  <div className="camera-wrapper">
                      <div className="btn-close" onClick={handleCameraClose}>
                          <FontAwesomeIcon icon={['far', 'times']} size="lg" />
                      </div>
                      {React.createElement(Camera as any, {
                          isFullscreen: true,
                          isDisplayStartCameraError: false,
                          onTakePhoto: handleTakePhoto,
                          onCameraError: handleCameraError,
                          idealFacingMode: FACING_MODES.ENVIRONMENT,
                          isImageMirror: false
                      })}
                  </div>,
                  document.body
              )
            : null;

        // Build the visible photo list: existing (not deleted) + pending adds
        const visibleExisting = existingPhotos.filter(p => !pendingDeleteIds.has(p.id));

        const getThumbnailSrc = (photo: Iphoto): string | undefined => {
            if (photo.thumbnailImage) return `data:image/jpeg;base64,${photo.thumbnailImage}`;
            if (photo.thumbnailUrl) return photo.thumbnailUrl;
            return undefined;
        };

        const photoTileStyle: React.CSSProperties = {
            position: 'relative',
            width: 'calc(50% - 4px)',
            aspectRatio: '1 / 1',
            borderRadius: '4px',
            border: '1px solid #ddd',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        };

        const overlayButtonStyle: React.CSSProperties = {
            position: 'absolute',
            bottom: '6px',
            right: '6px',
            width: '40px',
            height: '40px',
            borderRadius: '6px',
            fontSize: '18px',
            backgroundColor: 'rgba(208, 0, 0, 0.9)',
            color: '#fff',
            border: '2px solid rgba(255, 255, 255, 0.9)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
        };

        const reportOverlayStyle: React.CSSProperties = {
            position: 'absolute',
            top: '6px',
            left: '6px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '4px',
            padding: '2px 6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 500,
            userSelect: 'none'
        };

        const hasPhotos = visibleExisting.length > 0 || pendingAdds.length > 0;

        return (
            <div className="inline-photo-control" style={{ padding: '10px 0' }}>
                <div
                    style={{
                        display: readOnly ? 'none' : 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap'
                    }}
                >
                    <Button variant="primary" onClick={handleCameraOpen} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <img src={iconCamera} alt="" />
                        {t('manageWorkOrder:captureImage')}
                    </Button>
                    <FormLabel htmlFor={`inlineFileUpload-${jobCommentID || measurementPointID}`} style={{ cursor: 'pointer', margin: 0 }}>
                        <span className="btn btn-default" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={iconPhoto} alt="" />
                            {t('manageWorkOrder:attachImage')}
                        </span>
                        <FormControl
                            accept="image/png, image/jpeg"
                            id={`inlineFileUpload-${jobCommentID || measurementPointID}`}
                            type="file"
                            multiple
                            name="inline-photo"
                            ref={fileInputRef as any}
                            onChange={(event: any) => {
                                const fileInput = event.target as HTMLInputElement;
                                const files = fileInput.files as FileList;
                                if (files.length > 0) {
                                    handleFileChange(files);
                                }
                                event.target.value = '';
                            }}
                            style={{ display: 'none' }}
                        />
                    </FormLabel>
                </div>
                {hasPhotos && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                        {visibleExisting.map(photo => {
                            const src = getThumbnailSrc(photo);
                            if (!src) return null;
                            const isInReport = pendingReportChanges.get(photo.id) ?? photo.isInReport;
                            return (
                                <div
                                    key={photo.id}
                                    style={{ ...photoTileStyle, cursor: readOnly ? 'default' : 'pointer' }}
                                    onClick={readOnly ? undefined : () => toggleExistingReport(photo.id, isInReport)}
                                >
                                    <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                    <div style={reportOverlayStyle}>
                                        <input type="checkbox" checked={isInReport} readOnly style={{ margin: 0 }} />
                                        <span style={{ position: 'relative', top: '1px' }}>{t('manageWorkOrder:showInReportHeader')}</span>
                                    </div>
                                    {!readOnly && (
                                        <button type="button" title="Delete photo" style={overlayButtonStyle} onClick={(e) => { e.stopPropagation(); handleDeleteExisting(photo.id); }}>
                                            <FontAwesomeIcon icon={['far', 'trash-alt']} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                        {pendingAdds.map(pending => (
                            <div key={pending.id} style={{ ...photoTileStyle, cursor: 'pointer' }} onClick={() => togglePendingReport(pending.id)}>
                                <img src={pending.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                <div style={reportOverlayStyle}>
                                    <input type="checkbox" checked={pending.isInReport} readOnly style={{ margin: 0 }} />
                                    <span style={{ position: 'relative', top: '1px' }}>{t('manageWorkOrder:showInReportHeader')}</span>
                                </div>
                                <button type="button" title="Delete photo" style={overlayButtonStyle} onClick={(e) => { e.stopPropagation(); handleDeletePending(pending.id); }}>
                                    <FontAwesomeIcon icon={['far', 'trash-alt']} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {cameraComponent}
            </div>
        );
    }
);

export default InlinePhotoControl;
